import * as go from 'gojs';
import * as React from 'react';
import { Checkbox } from 'vet-bones/design-system/components';

import { getProjectResourceCardCheckboxTestId } from 'src/constants/testIds';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { removePartsFromSelection } from 'src/containers/ProjectDiagram/utils/removePartsFromSelection';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectHiddenModelsSelector } from 'src/selectors/projectHiddenModels';
import { projectIrisForModelIdSelector } from 'src/selectors/projectIrisForModelId';
import { RDFS_SUB_CLASS_OF_IRI } from 'src/templates/queries/iris';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectSidebarModelCheckboxProps = {
  modelId: string;
};

export const ProjectSidebarModelCheckbox: React.VFC<
  ProjectSidebarModelCheckboxProps
> = ({ modelId }) => {
  const diagram = useDiagram();
  const { projectId } = useRouterParams();
  const irisInModel = useReduxSelector(
    projectIrisForModelIdSelector(projectId, modelId)
  );
  const irisInModelCount = irisInModel.length;
  const hiddenModels = useReduxSelector(projectHiddenModelsSelector(projectId));
  const isHidden = hiddenModels?.includes(modelId) || false;

  const diagramSelection = useDiagramSelection();
  const selectedIrisInModelCount = React.useMemo(
    () =>
      diagramSelection
        .filter((item) => irisInModel.includes(item.data.value))
        .map((item) => item.data.value).count,
    [diagramSelection, irisInModel]
  );

  const isIndeterminate =
    selectedIrisInModelCount > 0 && selectedIrisInModelCount < irisInModelCount;

  const isChecked =
    selectedIrisInModelCount > 0 &&
    selectedIrisInModelCount === irisInModelCount &&
    !isIndeterminate;

  // get nodes & hierarchy links that are in the model
  const isNodeInModel = React.useCallback(
    (item: go.Part) => {
      if (item.data.value === RDFS_SUB_CLASS_OF_IRI) {
        const toValue = getModelConceptClassValue(item.data.to);
        const fromValue = getModelConceptClassValue(item.data.from);
        if (irisInModel.includes(toValue) || irisInModel.includes(fromValue)) {
          return true;
        }
      } else if (irisInModel.includes(item.data.value)) {
        return true;
      }

      return false;
    },
    [irisInModel]
  );

  const handleClick = React.useCallback(() => {
    if (!diagram || diagram.headless) {
      return;
    }

    // remove selection if already fully selected
    if (isChecked) {
      const selectedKeys: Set<go.Key> = new Set();
      diagramSelection.each((item) => {
        if (isNodeInModel(item)) {
          selectedKeys.add(item.key);
        }
      });
      commitDiagramModel(diagram, (_, diagram) =>
        removePartsFromSelection(selectedKeys)(diagram)
      );
    } else {
      // Add selection if not already selected
      const keysToSelect: go.Set<go.Part> = new go.Set();

      // add already selected nodes
      diagramSelection.each((item) => {
        keysToSelect.add(item);
      });

      // Add nodes that are in the model
      diagram.nodes.each((item) => {
        if (isNodeInModel(item)) {
          keysToSelect.add(item);
        }
      });

      commitDiagramWhileSkippingUndoManager(diagram, (_, diagram) =>
        setDiagramSelection(keysToSelect)(diagram)
      );
    }
  }, [diagram, isChecked, diagramSelection, isNodeInModel]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onClick={stopPropagation}>
      <Checkbox
        checked={isChecked}
        // The list view doesn't render the checkbox at all (VET-7253), but a
        // headless diagram is decided a render before the list view opens — keep
        // disabling it here so that frame can't select on a diagram that has no
        // canvas (VET-7007).
        disabled={isHidden || Boolean(diagram?.headless)}
        indeterminate={isIndeterminate}
        onClick={handleClick}
        size="md"
        testId={getProjectResourceCardCheckboxTestId(modelId)}
      />
    </div>
  );
};
